import * as React from "react";
import {
    Box,
    Card,
    CardContent,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import LeagueSpartanText from "../typography/LeagueSpartanText";
import CervinoText from "../typography/CervinoText";
import bgLeft from "../../assets/icons/SM_short_flower_white.png";
import bgRight from "../../assets/icons/SM_long_flower_white.png";
import SMButtonWhite from "../navigation/SMButtonWhite";

/**
 * SMOtherBrandsCard
 *
 * Props:
 * - title: string | ReactNode
 * - taglineLines: string[] (e.g. ["REAL FOOD", "REAL PEOPLE", "REAL MOMENTS"])
 * - cta: { label: string, onClick?: fn, href?: string, variant?: string, color?: string }
 *
 * - features: Array<{ id?: string, label: string }>
 *
 * - columns: Array<{
 *     key: string,
 *     header: ReactNode,     // e.g. <img .../> or text
 *     values: Array<boolean | ReactNode>, // length === features.length
 *   }>
 *
 * - whatMattersLabel?: string (default "WHAT MATTERS")
 *
 * - icons?: { truthy?: ReactNode, falsy?: ReactNode }
 * - renderCell?: ({ feature, column, value, rowIndex, colIndex }) => ReactNode
 *
 * - sx?: MUI sx
 * - leftSx?: MUI sx
 * - rightSx?: MUI sx
 * - backgroundSx?: MUI sx (for pattern / bg color on Card)
 */
export function SMOtherBrandsCard({
                                      title = "SAN MIGUEL VS.\nOTHER BRANDS",
                                      taglineLines = ["REAL FOOD", "REAL PEOPLE", "REAL MOMENTS"],

                                      whatMattersLabel = "WHAT \nMATTERS",
                                      features = [],
                                      columns = [],
                                      icons,
                                      renderCell,
                                      opacityIcons = 0.1,
                                      showFindUsButton = true,
                                      onFindUsClick,
                                      sx,
                                      leftSx,
                                      rightSx,
                                      backgroundSx,
                                  }) {
    const theme = useTheme();
    const isSmDown = useMediaQuery(theme.breakpoints.down("sm"));

    const truthyIcon =
        icons?.truthy ?? <CheckCircleRoundedIcon fontSize="inherit" />;
    const falsyIcon =
        icons?.falsy ?? <CancelOutlinedIcon fontSize="inherit"  />;

    const findUsButton = showFindUsButton ? (
        <Box
            sx={{
                mt: { xs: 3, md: 4, lg: 6 },
                display: "flex",
                justifyContent: "center",
            }}
        >
            <SMButtonWhite
                onClick={onFindUsClick}
                sx={{
                    minWidth: { xs: 250, sm: 250, md: 350, lg: 450 },
                    py: { xs: 0.5, sm: 0.5, md: 1, lg: 2 },
                }}
            >
                FIND US
            </SMButtonWhite>
        </Box>
    ) : null;

    // Guard: if values don’t match features length, we’ll still render,
    // but missing values become null.
    const safeValueAt = (col, idx) =>
        Array.isArray(col?.values) ? col.values[idx] : null;

    // Layout: left marketing + right comparison grid.
    // On small screens: stacks vertically.
    return (
        <Card
            elevation={0}
            sx={{
                borderRadius: 4,
                overflow: "hidden",
                position: "relative",
                isolation: "isolate",

                /* LEFT decorative image */
                "&::before": {
                    content: '""',
                    position: "absolute",
                    width: 560,
                    height: { xs: 400, sm: 520, md: 720, lg: 600, xl: 700},
                    left: { xs: -300, sm: -220, md: -220, lg: -170, xl: -170},
                    top: { xs: 500, sm: 0, md: -100, lg: -10, xl: -70},
                    backgroundImage: `url(${bgLeft})`,
                    backgroundRepeat: "no-repeat",
                    backgroundSize: "contain",
                    transform: "rotate(90deg)",
                    opacity: opacityIcons,
                    pointerEvents: "none",
                    zIndex: 0,
                },

                /* RIGHT decorative image */
                "&::after": {
                    content: '""',
                    position: "absolute",
                    width: 1000,
                    height: { xs: 700, sm: 750, md: 1500, lg: 1500 },
                    right: { xs: -450, sm: -450, md: -700, lg: -700 },
                    top: { xs: -550, sm: -550, md: -1000, lg: -1000 },
                    backgroundImage: `url(${bgRight})`,
                    backgroundRepeat: "no-repeat",
                    backgroundSize: "contain",
                    transform: "rotate(-135deg)",
                    opacity: opacityIcons,
                    pointerEvents: "none",
                    zIndex: 0,
                },

                /* content stays above */
                "& > *": {
                    position: "relative",
                    zIndex: 1,
                },

                ...backgroundSx,
                ...sx,
            }}
        >

            <CardContent
                sx={{
                    p: 0,
                    "&:last-child": { pb: 0 },
                }}
            >
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: isSmDown ? "1fr" : "1.1fr 1.4fr",
                        gap: 0,
                        minHeight: 220,
                    }}
                >
                    {/* LEFT: marketing block */}
                    <Box
                        sx={{
                            p: { xs: 3, md: 4 },
                            display: "grid",
                            gridTemplateRows: "auto 1fr auto",
                            alignContent: "stretch",
                            ...leftSx,
                        }}
                    >
                        <Typography
                            component="h2"
                            sx={{
                                whiteSpace: "pre-line",
                                fontWeight: 900,
                                letterSpacing: 1,
                                lineHeight: 0.95,
                                fontSize: { xs: 34, sm: 28, md: 38, lg: 60, xl: 88 },
                                textAlign: { xs: "center", md: "left" }
                            }}
                        >
                            {title}
                        </Typography>

                        <Box sx={{ mt: {xs: 2, sm: 4}, justifySelf: { xs: "left", md: "center" }, }}>
                            {taglineLines.map((line, idx) => (
                                <LeagueSpartanText
                                    key={`${line}-${idx}`}
                                    sx={{
                                        fontWeight: 800,
                                        letterSpacing: 2,
                                        fontSize: { xs: 24, sm: 28, md: 32, lg: 40, xl: 48 },
                                        lineHeight: 1,
                                    }}
                                >
                                    {line}
                                </LeagueSpartanText>
                            ))}
                        </Box>

                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "center",
                            }}
                        >
                            {/* TODO: NEED TO MAKE THIS BUTTON SCROLL DOWN TO FIND US */}
                            {!isSmDown && findUsButton}
                        </Box>
                    </Box>

                    {/* RIGHT: comparison grid */}
                    <Box
                        sx={{
                            paddingY: {sm: 3},
                            ...rightSx,
                        }}
                    >
                        <ComparisonGrid
                            whatMattersLabel={whatMattersLabel}
                            features={features}
                            columns={columns}
                            truthyIcon={truthyIcon}
                            falsyIcon={falsyIcon}
                            renderCell={renderCell}
                            safeValueAt={safeValueAt}
                        />

                        {isSmDown && (
                            <Box sx={{ gridColumn: "1 / -1", pb: 3 }}>
                                {findUsButton}
                            </Box>
                        )}
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );
}

function ComparisonGrid({
                            whatMattersLabel,
                            features,
                            columns,
                            truthyIcon,
                            falsyIcon,
                        }) {
    const templateCols = `1.2fr ${columns.map(() => "0.8fr").join(" ")}`;

    const resolveIcon = (type) => {
        if (type === "positive") return truthyIcon;
        if (type === "negative") return falsyIcon;
        return null;
    };

    return (
        <Box
            sx={{
                display: "grid",
                gridTemplateColumns: templateCols,
                alignItems: "center",
                columnGap: { xs: 2, md: 3 },
                rowGap: { xs: 1.5, md: 2 },
            }}
        >
            {/* Header row */}
            <CervinoText
                sx={{
                    fontWeight: 400,
                    fontSize: { xs: 16, sm: 18, md: 20, lg: 32, xl: 32 },
                    textTransform: "uppercase",
                    opacity: 0.9,
                    textAlign: "right",
                }}
            >
                {whatMattersLabel}
            </CervinoText>

            {columns.map((col) => (
                <Box
                    key={col.key}
                    sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        textAlign: "center",
                    }}
                >
                    {React.isValidElement(col.header) ? (
                        col.header
                    ) : (
                        <CervinoText
                            sx={{
                                fontWeight: 400,
                                fontSize: { xs: 16, sm: 18, md: 20, lg: 32, xl: 32 },
                                textTransform: "uppercase",
                                whiteSpace: "pre-line",
                            }}
                        >
                            {String(col.header)}
                        </CervinoText>
                    )}
                </Box>
            ))}

            {/* Feature rows */}
            {features.map((label, rowIndex) => (
                <React.Fragment key={rowIndex}>
                    <Typography
                        sx={{
                            fontWeight: 900,
                            textTransform: "uppercase",
                            fontSize: { xs: 14, sm: 18, md: 20, lg: 24, xl: 32 },
                            textAlign: "right",
                        }}
                    >
                        {label}
                    </Typography>

                    {columns.map((col) => (
                        <Box
                            key={`${rowIndex}-${col.key}`}
                            sx={{
                                display: "flex",
                                justifyContent: "center",
                                fontSize: {xs: 24, sm: 30, md: 36, lg: 40, xl: 48 },
                            }}
                        >
                            {resolveIcon(col.type)}
                        </Box>
                    ))}
                </React.Fragment>
            ))}
        </Box>
    );
}

